import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

export const templateApi = createApi({
  reducerPath: 'templateApi',
  baseQuery: fetchBaseQuery({
    baseUrl: API_URL, // .env has /api suffix, so routes are relative
    credentials: 'include',
    prepareHeaders: (headers) => {
      headers.set('Content-Type', 'application/json');
      return headers;
    },
  }),
  tagTypes: ['Template', 'TemplateVersion', 'TemplateDraft', 'TemplateLock', 'TemplateResponse'],
  endpoints: (builder) => ({
    // List Templates
    listTemplates: builder.query({
      query: ({ status, type, page = 1, per_page = 50 } = {}) => {
        const params = new URLSearchParams();
        if (status) params.append('status', status);
        if (type) params.append('type', type);
        params.append('page', page);
        params.append('per_page', per_page);
        return `/templates?${params.toString()}`;
      },
      providesTags: ['Template'],
    }),

    // Get Single Template
    getTemplate: builder.query({
      query: (id) => `/templates/${id}`,
      providesTags: (result, error, id) => [{ type: 'Template', id }],
    }),

    // Create Template
    createTemplate: builder.mutation({
      query: (data) => ({
        url: '/templates',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Template'],
    }),

    // Update Template
    updateTemplate: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/templates/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Template', id },
        'Template',
      ],
    }),

    // Delete Template
    deleteTemplate: builder.mutation({
      query: (id) => ({
        url: `/templates/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Template'],
    }),

    // Publish Version
    publishVersion: builder.mutation({
      query: ({ id, version_notes }) => ({
        url: `/templates/${id}/publish`,
        method: 'POST',
        body: { version_notes },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Template', id },
        { type: 'TemplateVersion', id },
        'Template', // Invalidate all template lists to refresh Published tab
      ],
    }),

    // Get Versions
    getVersions: builder.query({
      query: (id) => `/templates/${id}/versions`,
      providesTags: (result, error, id) => [{ type: 'TemplateVersion', id }],
    }),

    // Save Draft
    saveDraft: builder.mutation({
      query: ({ id, draft_data }) => ({
        url: `/templates/${id}/draft`,
        method: 'POST',
        body: { draft_data },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'TemplateDraft', id }],
    }),

    // Import Preview - parse file without creating template
    importPreview: builder.mutation({
      query: (formData) => ({
        url: '/templates/import-preview',
        method: 'POST',
        body: formData,
        // Don't set Content-Type for FormData
        prepareHeaders: (headers) => {
          headers.delete('Content-Type');
          return headers;
        },
      }),
    }),

    // Import CSV - create template with confirmed structure
    importCSV: builder.mutation({
      query: (data) => ({
        url: '/templates/import-csv',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Template'],
    }),

    // Export Template
    exportTemplate: builder.query({
      query: ({ id, format = 'json' }) => ({
        url: `/templates/${id}/export?format=${format}`,
        responseHandler: async (response) => {
          if (format === 'csv') {
            const blob = await response.blob();
            return blob;
          }
          return response.json();
        },
      }),
    }),

    // AI Assist
    aiAssist: builder.mutation({
      query: ({ id, prompt, context = null }) => ({
        url: `/templates/${id}/ai-assist`,
        method: 'POST',
        body: { prompt, context },
      }),
    }),

    // Get Locks
    getLocks: builder.query({
      query: (id) => `/templates/${id}/locks`,
      providesTags: (result, error, id) => [{ type: 'TemplateLock', id }],
    }),

    // Acquire Lock
    acquireLock: builder.mutation({
      query: ({ id, field_path }) => ({
        url: `/templates/${id}/locks`,
        method: 'POST',
        body: { field_path },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'TemplateLock', id }],
    }),

    // Release Lock
    releaseLock: builder.mutation({
      query: ({ id, lock_id }) => ({
        url: `/templates/${id}/locks/${lock_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'TemplateLock', id }],
    }),

    // Submit Response
    submitResponse: builder.mutation({
      query: ({ templateId, response_data, status, completion_percentage }) => ({
        url: `/templates/${templateId}/responses`,
        method: 'POST',
        body: { response_data, status, completion_percentage },
      }),
      invalidatesTags: (result, error, { templateId }) => [
        { type: 'TemplateResponse', id: templateId },
        'TemplateResponse',
      ],
    }),

    // Get Template Responses
    getTemplateResponses: builder.query({
      query: (templateId) => `/templates/${templateId}/responses`,
      providesTags: (result, error, templateId) => [{ type: 'TemplateResponse', id: templateId }],
    }),

    // Get All Responses (Completed tab)
    getAllResponses: builder.query({
      query: ({ status } = {}) => {
        const params = new URLSearchParams();
        if (status) params.append('status', status);
        return `/responses?${params.toString()}`;
      },
      providesTags: ['TemplateResponse'],
    }),

    // Get Single Response
    getResponse: builder.query({
      query: (responseId) => `/responses/${responseId}`,
      providesTags: (result, error, responseId) => [{ type: 'TemplateResponse', id: responseId }],
    }),
  }),
});

export const {
  useListTemplatesQuery,
  useGetTemplateQuery,
  useCreateTemplateMutation,
  useUpdateTemplateMutation,
  useDeleteTemplateMutation,
  usePublishVersionMutation,
  useGetVersionsQuery,
  useSaveDraftMutation,
  useImportPreviewMutation,
  useImportCSVMutation,
  useLazyExportTemplateQuery,
  useAiAssistMutation,
  useGetLocksQuery,
  useAcquireLockMutation,
  useReleaseLockMutation,
  useSubmitResponseMutation,
  useGetTemplateResponsesQuery,
  useGetAllResponsesQuery,
  useGetResponseQuery,
} = templateApi;
